/* SSi website – Welsh landing page "Where you'll use it" vignettes.
 *
 * Mirror of SpanishVignettes with Welsh illustrations and Welsh-flavoured
 * scenarios:
 *   1. Kitchen in Caernarfon – kettle + biscuit tin
 *   2. School gate          – school building + parent silhouette
 *   3. Pub in Aberystwyth   – pint of beer + bar
 *   4. Video call from abroad – laptop + Welsh dragon flag
 *
 * Each card reads from cy* tweak keys.
 */

export function WelshVignettes({ t }) {
  const cards = [
    {
      tag: t.cyVigTag1, title: t.cyVigTitle1, body: t.cyVigBody1,
      phrase: t.cyVigPhrase1, phraseEn: t.cyVigPhraseEn1,
      svg: <VigKettle/>,
      bg: '#FFE8D6',
    },
    {
      tag: t.cyVigTag2, title: t.cyVigTitle2, body: t.cyVigBody2,
      phrase: t.cyVigPhrase2, phraseEn: t.cyVigPhraseEn2,
      svg: <VigSchool/>,
      bg: '#FFF3C4',
    },
    {
      tag: t.cyVigTag3, title: t.cyVigTitle3, body: t.cyVigBody3,
      phrase: t.cyVigPhrase3, phraseEn: t.cyVigPhraseEn3,
      svg: <VigPub/>,
      bg: '#FCD9D4',
    },
    {
      tag: t.cyVigTag4, title: t.cyVigTitle4, body: t.cyVigBody4,
      phrase: t.cyVigPhrase4, phraseEn: t.cyVigPhraseEn4,
      svg: <VigVideoCall/>,
      bg: '#D6EADF',
    },
  ];

  return (
    <section className="ssi-cyVig" style={cyVigStyles.wrap}>
      <div style={cyVigStyles.inner}>
        <div style={cyVigStyles.head}>
          <div style={cyVigStyles.kicker}>{t.cyVigKicker}</div>
          <h2 style={cyVigStyles.h2}>{t.cyVigTitle}</h2>
          <p style={cyVigStyles.lede}>{t.cyVigLede}</p>
        </div>

        <ul className="ssi-cyVig__grid" style={cyVigStyles.grid}>
          {cards.map((c, i) => (
            <li key={i} style={{...cyVigStyles.card, background: c.bg}}>
              <div style={cyVigStyles.illo}>{c.svg}</div>
              <div style={cyVigStyles.tag}>{c.tag}</div>
              <h3 style={cyVigStyles.cardH}>{c.title}</h3>
              <p style={cyVigStyles.cardBody}>{c.body}</p>
              <div style={cyVigStyles.phraseBlock}>
                <div style={cyVigStyles.phrase}>&ldquo;{c.phrase}&rdquo;</div>
                <div style={cyVigStyles.phraseEn}>{c.phraseEn}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────── Welsh-flavoured vignette SVGs ─────────────── *
 * Brand palette only (deepRed / brandRed / gold / cream).
 * Same density / weight as the Spanish set so they sit side-by-side.
 */

function VigKettle() {
  return (
    <svg viewBox="0 0 120 80" width="100%" height="100%" fill="none">
      {/* counter line */}
      <line x1="6" y1="68" x2="114" y2="68" stroke="#490300" strokeWidth="1.5"/>
      {/* kettle body */}
      <path d="M40 68 L36 40 Q36 32 44 30 L72 30 Q80 32 80 40 L76 68 Z" fill="#DB1E17" stroke="#490300" strokeWidth="1.5"/>
      {/* spout */}
      <path d="M36 38 L24 30 L26 36 L34 42 Z" fill="#DB1E17" stroke="#490300" strokeWidth="1.5"/>
      {/* lid */}
      <ellipse cx="58" cy="30" rx="16" ry="3" fill="#900600" stroke="#490300" strokeWidth="1.5"/>
      <circle cx="58" cy="26" r="2.5" fill="#FEC902" stroke="#490300" strokeWidth="1"/>
      {/* handle */}
      <path d="M80 38 Q92 38 92 50 L86 50 Q86 44 80 44" fill="none" stroke="#490300" strokeWidth="1.5"/>
      {/* steam */}
      <path d="M28 22 Q26 18 30 14 Q34 10 32 6" stroke="#490300" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity=".5"/>
      <path d="M22 22 Q20 16 24 14" stroke="#490300" strokeWidth="1.2" fill="none" strokeLinecap="round" opacity=".5"/>
      {/* biscuit / tin */}
      <rect x="92" y="50" width="20" height="18" fill="#FEC902" stroke="#490300" strokeWidth="1.5"/>
      <rect x="92" y="50" width="20" height="4" fill="#900600"/>
      <line x1="98" y1="58" x2="106" y2="58" stroke="#490300" strokeWidth="1"/>
      <line x1="98" y1="62" x2="106" y2="62" stroke="#490300" strokeWidth="1"/>
    </svg>
  );
}

function VigSchool() {
  return (
    <svg viewBox="0 0 120 80" width="100%" height="100%" fill="none">
      {/* ground */}
      <line x1="0" y1="74" x2="120" y2="74" stroke="#490300" strokeWidth="1.5"/>
      {/* building */}
      <rect x="20" y="36" width="60" height="38" fill="#FEC902" stroke="#490300" strokeWidth="1.5"/>
      {/* roof */}
      <path d="M16 38 L50 18 L84 38 Z" fill="#900600" stroke="#490300" strokeWidth="1.5"/>
      {/* clock / window */}
      <circle cx="50" cy="32" r="4" fill="#fff" stroke="#490300" strokeWidth="1"/>
      <line x1="50" y1="32" x2="50" y2="29" stroke="#490300" strokeWidth="1"/>
      <line x1="50" y1="32" x2="52" y2="33" stroke="#490300" strokeWidth="1"/>
      {/* door */}
      <rect x="44" y="54" width="12" height="20" fill="#490300"/>
      <circle cx="53" cy="64" r=".8" fill="#FEC902"/>
      {/* windows */}
      <rect x="26" y="46" width="12" height="10" fill="#fff" stroke="#490300" strokeWidth="1"/>
      <line x1="32" y1="46" x2="32" y2="56" stroke="#490300" strokeWidth=".8"/>
      <line x1="26" y1="51" x2="38" y2="51" stroke="#490300" strokeWidth=".8"/>
      <rect x="62" y="46" width="12" height="10" fill="#fff" stroke="#490300" strokeWidth="1"/>
      <line x1="68" y1="46" x2="68" y2="56" stroke="#490300" strokeWidth=".8"/>
      <line x1="62" y1="51" x2="74" y2="51" stroke="#490300" strokeWidth=".8"/>
      {/* parent + child */}
      <circle cx="96" cy="58" r="4" fill="#FFE8D6" stroke="#490300" strokeWidth="1"/>
      <rect x="93" y="62" width="6" height="12" fill="#DB1E17"/>
      <circle cx="104" cy="64" r="2.5" fill="#FFE8D6" stroke="#490300" strokeWidth="1"/>
      <rect x="102" y="67" width="4" height="7" fill="#490300"/>
      {/* hand-hold */}
      <line x1="99" y1="68" x2="102" y2="69" stroke="#490300" strokeWidth="1"/>
    </svg>
  );
}

function VigPub() {
  return (
    <svg viewBox="0 0 120 80" width="100%" height="100%" fill="none">
      {/* bar counter */}
      <rect x="6" y="60" width="108" height="14" fill="#490300"/>
      <rect x="6" y="56" width="108" height="4" fill="#900600"/>
      {/* pint glass */}
      <path d="M44 24 L72 24 L70 60 L46 60 Z" fill="none" stroke="#490300" strokeWidth="1.5"/>
      {/* beer */}
      <path d="M46 32 L70 32 L68 60 L48 60 Z" fill="#FEC902"/>
      {/* foam */}
      <path d="M44 24 Q48 18 52 24 Q56 18 60 24 Q64 18 68 24 Q72 22 72 28 L44 28 Z" fill="#fff" stroke="#490300" strokeWidth="1.5"/>
      {/* bubbles */}
      <circle cx="52" cy="48" r="1.5" fill="#fff" opacity=".7"/>
      <circle cx="60" cy="42" r="1.2" fill="#fff" opacity=".7"/>
      <circle cx="64" cy="52" r="1.2" fill="#fff" opacity=".7"/>
      {/* daffodil-ish flower behind */}
      <circle cx="92" cy="34" r="6" fill="#FEC902" stroke="#490300" strokeWidth="1"/>
      <circle cx="92" cy="34" r="2" fill="#DB1E17"/>
      <line x1="92" y1="40" x2="92" y2="58" stroke="#490300" strokeWidth="1.2"/>
      <path d="M92 50 Q86 48 84 54" stroke="#490300" strokeWidth="1" fill="none"/>
      {/* coaster on bar */}
      <ellipse cx="22" cy="62" rx="8" ry="2" fill="#900600"/>
    </svg>
  );
}

function VigVideoCall() {
  return (
    <svg viewBox="0 0 120 80" width="100%" height="100%" fill="none">
      {/* laptop base */}
      <path d="M14 64 L106 64 L114 72 L6 72 Z" fill="#490300"/>
      {/* laptop screen */}
      <rect x="20" y="14" width="80" height="50" fill="#fff" stroke="#490300" strokeWidth="1.5"/>
      {/* screen tile (Welsh flag-ish) */}
      <rect x="24" y="18" width="36" height="22" fill="#FFE8D6"/>
      <circle cx="42" cy="27" r="6" fill="#FFE8D6" stroke="#490300" strokeWidth="1"/>
      <circle cx="40" cy="26" r="1" fill="#490300"/>
      <circle cx="44" cy="26" r="1" fill="#490300"/>
      <path d="M40 30 Q42 32 44 30" stroke="#490300" strokeWidth="1" fill="none" strokeLinecap="round"/>
      {/* second tile with dragon-ish silhouette */}
      <rect x="62" y="18" width="36" height="22" fill="#fff"/>
      <rect x="62" y="18" width="36" height="11" fill="#fff"/>
      <rect x="62" y="29" width="36" height="11" fill="#3F8C5B"/>
      <path d="M68 24 Q74 20 80 24 Q84 26 88 24 Q90 26 88 28 Q86 30 80 28 Q74 30 70 28 Q68 28 68 26 Z" fill="#DB1E17"/>
      {/* caption bar */}
      <rect x="24" y="44" width="72" height="4" fill="#490300" opacity=".15"/>
      <rect x="24" y="50" width="50" height="4" fill="#490300" opacity=".15"/>
      <rect x="24" y="56" width="40" height="4" fill="#490300" opacity=".15"/>
      {/* speech dots */}
      <circle cx="84" cy="58" r="1.4" fill="#DB1E17"/>
      <circle cx="89" cy="58" r="1.4" fill="#DB1E17"/>
      <circle cx="94" cy="58" r="1.4" fill="#DB1E17"/>
    </svg>
  );
}

const cyVigStyles = {
  wrap: { background: 'var(--bg-2, #fff)', padding: '96px 32px 96px' },
  inner: { maxWidth: 1240, margin: '0 auto' },
  head: { maxWidth: 760, marginBottom: 56 },
  kicker: {
    fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase',
    color: 'var(--brandRed)', fontWeight: 700, marginBottom: 14,
  },
  h2: {
    fontFamily: 'var(--font-display)', fontWeight: 700,
    fontSize: 52, lineHeight: 1.08,
    letterSpacing: '-.015em', margin: '0 0 18px',
  },
  lede: { fontFamily: 'var(--font-body)', fontSize: 18, lineHeight: 1.55, color: 'var(--grey)', margin: 0, textWrap: 'pretty', maxWidth: '60ch' },

  grid: {
    listStyle: 'none', margin: 0, padding: 0,
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 22,
  },
  card: {
    border: '1px solid rgba(73,3,0,.12)',
    borderRadius: 18,
    padding: '22px 22px 24px',
    display: 'flex', flexDirection: 'column', gap: 10,
    boxShadow: '0 10px 24px -18px rgba(73,3,0,.35)',
  },
  illo: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid rgba(73,3,0,.12)',
    padding: 10,
    marginBottom: 6,
    aspectRatio: '3 / 2',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  tag: {
    display: 'inline-block',
    alignSelf: 'flex-start',
    fontSize: 11, fontWeight: 700, letterSpacing: '.12em',
    textTransform: 'uppercase',
    color: 'var(--brandRed)',
    background: '#fff',
    padding: '4px 10px', borderRadius: 999,
    border: '1px solid rgba(73,3,0,.14)',
  },
  cardH: {
    fontFamily: 'var(--font-display)', fontWeight: 700,
    fontSize: 20, lineHeight: 1.2,
    margin: '4px 0 2px', color: 'var(--fg-1)',
    letterSpacing: '-.005em',
  },
  cardBody: {
    fontFamily: 'var(--font-body)',
    fontSize: 14, lineHeight: 1.5,
    color: 'var(--fg-1)', margin: 0,
    textWrap: 'pretty',
  },
  phraseBlock: {
    marginTop: 'auto',
    paddingTop: 14,
    borderTop: '1px dashed rgba(73,3,0,.3)',
  },
  phrase: {
    fontFamily: 'var(--font-display)', fontStyle: 'italic',
    fontSize: 16, color: 'var(--deepRed)',
    lineHeight: 1.35, marginBottom: 2,
  },
  phraseEn: {
    fontFamily: 'var(--font-body)',
    fontSize: 12, color: 'var(--grey)',
    fontStyle: 'italic',
  },
};
